import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, XCircle, LoaderCircle, UserCheck, UserX,
  FileText, ThumbsUp, ThumbsDown, Clock, History, ScrollText,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200" },
};

const loanStatusConfig: Record<string, { label: string; color: string }> = {
  pending_guarantor_approval: { label: "Awaiting Guarantors", color: "bg-purple-100 text-purple-800" },
  pending_approval: { label: "Pending Approval", color: "bg-blue-100 text-blue-800" },
  approved_by_leaders: { label: "Approved by Leaders", color: "bg-indigo-100 text-indigo-800" },
  pending: { label: "Processing", color: "bg-sky-100 text-sky-800" },
  ready_for_disbursement: { label: "Ready for Disbursement", color: "bg-teal-100 text-teal-800" },
  disbursed: { label: "Disbursed", color: "bg-green-100 text-green-800" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800" },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-800" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
};

function formatCurrency(amount: number | string | undefined | null): string {
  const num = Number(amount) || 0;
  return "RWF " + num.toLocaleString("en-US");
}

type Tab = "pending" | "history";

export default function MyGuarantorRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("pending");
  const [comments, setComments] = useState<Record<number, string>>({});
  const [respondingId, setRespondingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery(
    ["my-guarantor-requests", user?.id],
    async () => {
      const { data: res } = await api.get("/loans/my/guarantor-requests");
      return res.data || res.requests || res || [];
    },
    { enabled: !!user?.id }
  );

  const respondMutation = useMutation(
    async ({ id, action, comment }: { id: number; action: "approve" | "reject"; comment?: string }) => {
      const payload = { action, comment };
      console.log("[Guarantor Respond] Payload:", payload);
      const { data: res } = await api.patch(`/loans/guarantors/${id}/respond`, payload);
      return res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["my-guarantor-requests", user?.id]);
        toast.success("Response submitted successfully");
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to submit response");
      },
      onSettled: () => {
        setRespondingId(null);
      },
    }
  );

  const handleRespond = (id: number, action: "approve" | "reject") => {
    if (respondingId === id) return;
    setRespondingId(id);
    respondMutation.mutate({ id, action, comment: comments[id] || undefined });
  };

  const requests = Array.isArray(data) ? data : [];
  const pendingRequests = requests.filter((r: any) => r.status === "pending");
  const historyRequests = requests.filter((r: any) => r.status !== "pending");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoaderCircle className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Guarantor Requests</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and manage your guarantor commitments
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab("pending")}
          className={cn(
            "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
            activeTab === "pending"
              ? "border-[#009A44] text-[#009A44]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
        >
          <Clock className="h-4 w-4" />
          Pending Approvals
          {pendingRequests.length > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold">
              {pendingRequests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={cn(
            "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
            activeTab === "history"
              ? "border-[#009A44] text-[#009A44]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
        >
          <History className="h-4 w-4" />
          My Guarantor History
          {historyRequests.length > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
              {historyRequests.length}
            </span>
          )}
        </button>
      </div>

      {/* Pending Tab */}
      {activeTab === "pending" && (
        <>
          {pendingRequests.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <UserCheck className="h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500 font-medium">No pending requests</p>
                <p className="text-sm text-slate-400 mt-1">
                  You have no guarantor requests awaiting your response
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingRequests.map((req: any) => {
                const isProcessing = respondingId === req.id;
                return (
                  <Card key={req.id} className="overflow-hidden border-yellow-200">
                    <CardHeader className="pb-3 bg-yellow-50/50">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            {req.loan?.loanCode || `Loan #${req.loanId || req.loan?.id}`}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            Requested by <span className="font-medium text-slate-700">{req.loan?.applicant?.fullName || req.loan?.applicantName || "Unknown Applicant"}</span>
                          </CardDescription>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 px-3 py-1 text-xs font-medium">
                          Action Required
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-slate-500">Loan Amount</p>
                          <p className="text-sm font-semibold text-slate-800">{formatCurrency(req.loan?.amount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Request Date</p>
                          <p className="text-sm text-slate-700">{req.createdAt ? format(new Date(req.createdAt), "MMM d, yyyy HH:mm") : "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Your Savings</p>
                          <p className="text-sm font-semibold text-slate-800">{formatCurrency(req.savings || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Term</p>
                          <p className="text-sm text-slate-700">{req.loan?.termMonths || "N/A"} months</p>
                        </div>
                      </div>
                      {req.loan?.purpose && (
                        <p className="text-xs text-slate-500 mb-3">
                          Purpose: <span className="text-slate-700">{req.loan.purpose}</span>
                        </p>
                      )}
                      <div className="space-y-3">
                        <Textarea
                          placeholder="Add a comment (optional)..."
                          value={comments[req.id] || ""}
                          onChange={(e) => setComments((prev) => ({ ...prev, [req.id]: e.target.value }))}
                          className="min-h-[60px] text-sm"
                          disabled={isProcessing}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleRespond(req.id, "approve")}
                            disabled={isProcessing}
                          >
                            {isProcessing ? (
                              <LoaderCircle className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <ThumbsUp className="h-4 w-4 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => handleRespond(req.id, "reject")}
                            disabled={isProcessing}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/member/loans/${req.loan?.loanCode || req.loan?.id}`)}
                          >
                            View Loan
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <>
          {historyRequests.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ScrollText className="h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500 font-medium">No guarantor history</p>
                <p className="text-sm text-slate-400 mt-1">
                  You haven't guaranteed any loans yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {historyRequests.map((req: any) => {
                const cfg = statusConfig[req.status] || statusConfig.pending;
                const loanCfg = loanStatusConfig[req.loan?.status] || { label: req.loan?.status || "Unknown", color: "bg-slate-100 text-slate-800" };
                return (
                  <Card key={req.id} className="overflow-hidden hover:shadow-sm transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <Users className="h-5 w-5 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">
                              {req.loan?.loanCode || `Loan #${req.loanId || req.loan?.id}`}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              Applicant: {req.loan?.applicant?.fullName || req.loan?.applicantName || "Unknown Applicant"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={`${cfg.color} border px-2 py-0.5 text-xs`}>
                            {cfg.label}
                          </Badge>
                          <Badge className={`${loanCfg.color} px-2 py-0.5 text-xs`}>
                            {loanCfg.label}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400">Amount</span>
                          <p className="font-medium text-slate-800">{formatCurrency(req.loan?.amount)}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">Your Response</span>
                          <p className={cn("font-medium", req.status === "approved" ? "text-green-600" : "text-red-600")}>
                            {req.status === "approved" ? "Approved" : "Rejected"}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400">Response Date</span>
                          <p className="font-medium text-slate-800">
                            {req.approvedAt ? format(new Date(req.approvedAt), "MMM d, yyyy HH:mm") : "N/A"}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400">Your Savings at Time</span>
                          <p className="font-medium text-slate-800">{formatCurrency(req.savings || 0)}</p>
                        </div>
                      </div>

                      {req.comment && (
                        <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600 italic">
                          "{req.comment}"
                        </div>
                      )}

                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={() => navigate(`/member/loans/${req.loan?.loanCode || req.loan?.id}`)}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          View Loan Details
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={() => navigate(`/member/loans/apply`)}
                        >
                          <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                          Apply for New Loan
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
